import { AlertTriangle } from "lucide-react";
import LegalShell, { LegalSection } from "@/components/LegalShell";

/**
 * Términos y Condiciones — public, static. Placeholders are LITERAL and visible
 * so the doc cannot be published with legal blanks unfilled. Pricing figures are
 * the real server-defined amounts (see backend/billing.py) and must stay in sync.
 */
export default function TermsPage() {
  return (
    <LegalShell title="Términos y Condiciones" updatedLabel="Última actualización: 2026-07-14">
      <p>
        Estos Términos y Condiciones (los &ldquo;Términos&rdquo;) rigen el acceso y uso de The Trading Dock (la
        &ldquo;App&rdquo;), operada por{" "}
        <span className="text-white font-semibold">Pryma Wellness Group Inc.</span>{" "}
        (&ldquo;nosotros&rdquo;). Al crear una cuenta o usar la App, aceptas estos Términos en su totalidad. Si no estás de
        acuerdo, no uses la App.
      </p>

      <LegalSection id="servicio" heading="1. Descripción del servicio">
        <p>
          The Trading Dock es una herramienta de <span className="text-white font-semibold">organización de finanzas
          personales</span>. Te permite registrar y visualizar tus cuentas, transacciones, tarjetas y deudas en un solo
          panel, con soporte multi-moneda (DOP, USD, EUR, USDT y otras) y conversión de tasas. Incluye escaneo de recibos
          por OCR con inteligencia artificial para extraer los datos de una foto y registrarlos como transacción, además
          de metas, presupuestos, categorías personalizadas, movimientos recurrentes, conciliación y hogares compartidos
          (Familia).
        </p>
        <p>
          La App <span className="text-white font-semibold">no se conecta directamente a ningún banco ni institución
          financiera</span>. Todos los datos financieros los ingresas tú manualmente o mediante el escaneo de recibos.
        </p>
      </LegalSection>

      <LegalSection id="planes" heading="2. Planes, precios y cobros">
        <p>Ofrecemos los siguientes planes. Los montos están en dólares estadounidenses (USD):</p>
        <ul className="list-disc pl-5 space-y-1.5 marker:text-violet-400">
          <li>
            <span className="text-white font-semibold">Pro</span> — $5 / mes o $48 / año. La oferta introductoria de $1
            los primeros 7 días aplica solo al plan mensual (ver mecánica exacta abajo).
          </li>
          <li>
            <span className="text-white font-semibold">Familia</span> — $9 / mes o $84 / año. Misma oferta introductoria
            de $1 los primeros 7 días en el plan mensual.
          </li>
          <li>
            <span className="text-white font-semibold">Acceso de por vida (Lifetime)</span> — pago único de $100 (oferta
            de lanzamiento; precio regular $250). Sin renovaciones ni cobros recurrentes.
          </li>
        </ul>
        <p>
          <span className="text-white font-semibold">Mecánica exacta de la oferta de $1 (planes mensuales Pro/Familia):</span>{" "}
          hoy pagas $1.00 como cargo único. Tienes acceso completo durante 7 días sin ningún otro cobro. Si no cancelas
          antes de que termine ese período, al día 7 se cobra automáticamente{" "}
          <span className="text-white font-semibold">$4 (Pro) o $8 (Familia)</span> — el precio normal del plan menos el
          crédito de $1 ya cobrado, no el precio completo. A partir de esa fecha, cada renovación mensual siguiente
          (mes 2 en adelante) se cobra al precio normal del plan: $5/mes (Pro) o $9/mes (Familia). No hay prorrateo en
          ningún cobro — cada cargo es un monto fijo completo. Todos los pagos se procesan de forma segura a través de{" "}
          <span className="text-white font-semibold">Stripe</span>. Nunca almacenamos los datos de tu tarjeta.
        </p>
      </LegalSection>

      <LegalSection id="reembolsos" heading="3. Cancelación y política de reembolso">
        <p>
          Puedes <span className="text-white font-semibold">cancelar en cualquier momento</span> desde la App, en tu perfil
          (sección de facturación). Al cancelar, conservas el acceso hasta el final del período ya pagado y no se generan
          cobros posteriores.
        </p>
        <p>
          <span className="text-white font-semibold">No emitimos reembolsos prorrateados</span> por períodos parciales ya
          facturados, salvo en caso de un error de cobro imputable a nosotros. El pago único de acceso de por vida
          (Lifetime) no es reembolsable salvo error nuestro.
        </p>
      </LegalSection>

      <LegalSection id="disclaimer" heading="4. Aviso importante — no es asesoría">
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-white font-semibold">
                The Trading Dock es únicamente una herramienta de organización de finanzas personales.
              </p>
              <p className="text-amber-100/90">
                La App <span className="font-semibold">NO proporciona asesoría financiera, de inversión, tributaria ni
                legal</span> de ningún tipo. La información y visualizaciones que genera son para tu organización personal
                y no constituyen una recomendación para comprar, vender o mantener ningún activo. Consulta a un profesional
                autorizado antes de tomar decisiones financieras, de inversión o fiscales.
              </p>
            </div>
          </div>
        </div>
      </LegalSection>

      <LegalSection id="responsabilidad" heading="5. Limitación de responsabilidad">
        <p>
          La App se ofrece &ldquo;tal cual&rdquo; y &ldquo;según disponibilidad&rdquo;, sin garantías de ningún tipo. En la
          máxima medida permitida por la ley aplicable, no seremos responsables por pérdidas o daños indirectos,
          incidentales, especiales o consecuentes, ni por pérdida de datos, ganancias o oportunidades, derivados del uso o
          la imposibilidad de uso de la App. Eres responsable de la exactitud de los datos que ingresas y de las decisiones
          que tomes con base en ellos.
        </p>
      </LegalSection>

      <LegalSection id="jurisdiccion" heading="6. Ley aplicable y jurisdicción">
        <p>
          Estos Términos se rigen por las leyes de{" "}
          <span className="text-white font-semibold">Delaware, Estados Unidos</span>, y cualquier
          controversia se someterá a los tribunales competentes de dicha jurisdicción.
        </p>
      </LegalSection>

      <LegalSection id="contacto" heading="7. Contacto">
        <p>
          Para cualquier consulta sobre estos Términos, escríbenos a{" "}
          <span className="text-white font-semibold">soporte@thetradingdock.com</span>.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
